import type { MealPlanDto } from '@fewerbags/schema';
import { formatMonthLabel, isSameMonth, isToday, monthGridDays } from '../../lib/planDates';
import { isLeftovers, planTitle } from '../../lib/plans';
import { PlanColorDot } from './PlanColorDot';

const MAX_CHIPS_PER_DAY = 3;

/** A month's meals, grouped by local calendar date. */
function plansByDate(plans: readonly MealPlanDto[]): Map<string, MealPlanDto[]> {
  const byDate = new Map<string, MealPlanDto[]>();
  for (const plan of plans) {
    const list = byDate.get(plan.date) ?? [];
    list.push(plan);
    byDate.set(plan.date, list);
  }
  for (const list of byDate.values()) {
    list.sort(
      (a, b) => (a.serveAt ?? '').localeCompare(b.serveAt ?? '') || a.id.localeCompare(b.id),
    );
  }
  return byDate;
}

/**
 * A calendar-grid month view: compact meal chips per day, click a day to jump
 * the week editor there. `plans` should already cover the whole grid (the
 * caller queries `monthGridRange`, which can spill a few days into the
 * neighbouring months).
 */
export function MonthView({
  monthIso,
  plans,
  onSelectDay,
}: {
  monthIso: string;
  plans: readonly MealPlanDto[];
  onSelectDay: (date: string) => void;
}) {
  const days = monthGridDays(monthIso);
  const byDate = plansByDate(plans);
  const today = new Date();

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold">{formatMonthLabel(monthIso)}</h2>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold tracking-wide text-muted uppercase">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <ol className="grid grid-cols-7 gap-1">
        {days.map((date) => {
          const inMonth = isSameMonth(date, monthIso);
          const dayPlans = byDate.get(date) ?? [];
          const chips = dayPlans.slice(0, MAX_CHIPS_PER_DAY);
          const overflow = dayPlans.length - chips.length;
          return (
            <li key={date}>
              <button
                type="button"
                onClick={() => onSelectDay(date)}
                aria-label={`${date}${dayPlans.length ? `, ${dayPlans.length} meal${dayPlans.length === 1 ? '' : 's'}` : ''}`}
                className={`flex min-h-20 w-full flex-col gap-0.5 rounded-xl border p-1 text-left align-top transition-[border-color,box-shadow] sm:min-h-28 sm:p-1.5 ${
                  inMonth
                    ? 'border-line bg-surface shadow-sm shadow-brand/5'
                    : 'border-line/50 bg-cream/50'
                } ${isToday(date, today) ? 'ring-2 ring-accent' : ''} hover:border-brand/40 hover:shadow-md`}
              >
                <span
                  className={`inline-flex size-6 items-center justify-center rounded-full text-xs ${
                    isToday(date, today)
                      ? 'bg-brand font-bold text-white'
                      : inMonth
                        ? 'font-semibold text-ink'
                        : 'text-muted'
                  }`}
                >
                  {Number(date.slice(8, 10))}
                </span>
                <span className="flex flex-col gap-0.5">
                  {chips.map((plan) => (
                    <span
                      key={plan.id}
                      className="flex items-center gap-1 truncate rounded-md bg-brand-soft/60 px-1 py-0.5 text-[11px] text-ink"
                    >
                      <PlanColorDot
                        colorIndex={plan.recipes[0]?.colorIndex ?? 0}
                        size={6}
                        className="shrink-0"
                      />
                      <span className={`truncate ${isLeftovers(plan) ? 'italic' : ''}`}>
                        {planTitle(plan)}
                      </span>
                    </span>
                  ))}
                  {overflow > 0 ? (
                    <span className="text-[11px] text-muted">+{overflow} more</span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
